/**
 * BriefingLoadingState — the branded loading surface that fires
 * between navigation and the first paint of the Briefing canvas.
 *
 * The default React `<Loader2 className="animate-spin" />` here was a
 * black void with a tiny teal spinner — the worst first impression in
 * the platform for a moment that's supposed to feel cinematic. This
 * replaces it with three layers stacked vertically:
 *
 *   1. WAVE logo (matches AuthShell's wordmark + Waves icon mark so
 *      the brand recognizes "I'm in the same product")
 *   2. Loading copy ("Building your wave briefing…") — verb-first,
 *      anticipatory, customer vocabulary
 *   3. Animated waveform line — 4 sine-wave bars with a pulse cycle
 *      that conveys "the system is doing work right now"
 *
 * No spinners. No generic skeletons. The brand feels they're about to
 * see something built.
 */
import React from 'react';
import { Waves } from 'lucide-react';

const TEAL = '#00D4FF';
const PINK = '#FF4D94';

export default function BriefingLoadingState() {
  return (
    <div
      data-testid="briefing-loading"
      className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6 text-center"
    >
      {/* WAVE logo — identical treatment to AuthShell so the brand
          immediately recognizes this is WAVE doing something, not a
          generic network screen. */}
      <div
        className="flex items-center gap-3 mb-8"
        data-testid="briefing-loading-logo"
      >
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{
            background: `${TEAL}1A`,
            boxShadow:  `0 0 28px ${TEAL}55`,
          }}
        >
          <Waves className="h-6 w-6" style={{ color: TEAL }} />
        </div>
        <span className="text-2xl font-black tracking-tight text-white">
          WAVE
        </span>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
          Brand&nbsp;DSP
        </span>
      </div>

      {/* Loading copy — verb-first, anticipatory */}
      <p
        className="text-[14px] font-bold text-white mb-1.5"
        data-testid="briefing-loading-headline"
      >
        Building your wave briefing
      </p>
      <p className="text-[11px] text-gray-500 mb-8 max-w-sm">
        Reading your data room, scoring creators, validating cohort integrity…
      </p>

      {/* Animated waveform — 4 vertical bars whose heights pulse on a
          staggered cycle so the line reads as "system at work." Pure
          CSS keyframes; no JS animation overhead. */}
      <div
        className="flex items-end gap-1.5 h-8"
        data-testid="briefing-loading-waveform"
        aria-label="loading"
        role="status"
      >
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <span
            key={i}
            className="block w-[3px] rounded-full"
            style={{
              background:      i % 2 === 0 ? TEAL : PINK,
              height:          '12px',
              animation:       'briefingWaveformPulse 1.1s ease-in-out infinite',
              animationDelay:  `${i * 110}ms`,
              opacity:         0.85,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes briefingWaveformPulse {
          0%, 100% {
            height: 6px;
            opacity: 0.4;
          }
          50% {
            height: 28px;
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
